import {
  AlertMsgNoShapeFile,
  AlertMsgNoSpreadsheet,
} from '@/alert-string-constants';
import REGION_QUERY from '@/api/ariadne/queries/region-query';
import * as appActions from '@/app/app-actions';
import {
  LOADING_SCREEN_LOADING_TEXT,
  LOADING_SCREEN_REFETCH_TEXT,
  NAV_ITEMS,
  STRORACLE_ENDPOINTS,
} from '@/constants';
import * as dashboardActions from '@/dashboard/dashboard-actions';
import * as dataActions from '@/data/data-actions';
import * as dataSelectors from '@/data/data-selectors';
import { ERROR_TYPES } from '@/error-boundaries/errors';
import * as mapActions from '@/map/map-actions';
import * as userActions from '@/user/user-actions';
import * as userSelectors from '@/user/user-selectors';
import { userSessionMgr } from '@/user/user-session-mgr';
import { client as ariadneGqlClient } from '@/utils/gqlapi';
import { getPaginatedData } from '@/utils/paginated-fetch';
import { signS3Url } from '@/utils/sign-s3-url';
import { strOracleApiSender } from '@/utils/stroracle-api-sender';
import { toaster } from '@/utils/toaster';
import * as Sentry from '@sentry/browser';
import axios, { AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import { Set as ImmutableSet } from 'immutable';
import { compact } from 'lodash';
import { SagaIterator } from 'redux-saga';
import { all, call, cancel, put, select, takeLatest } from 'redux-saga/effects';
import { BaseSender } from '../utils/base-sender';
import { getPlaceSpecificUrl } from '../utils/place-detection';
import { strOracleDefaultApiSender } from '../utils/stroracle-default-api-sender';

interface Property {
  apn?: string;
  violations?: unknown[];
  onlinePlatforms?: unknown[];
  [key: string]: unknown;
}

interface Complaint {
  apn?: string;
  [key: string]: unknown;
}

interface StatsData {
  shape: string;
  zoningFiles?: string[];
  [key: string]: unknown;
}

interface UserRightsData {
  [key: string]: unknown;
}

interface TranslationsData {
  [key: string]: unknown;
}

interface RentalscapeConfig {
  [key: string]: unknown;
}

interface GeoJsonFeatureProperties {
  region?: string | number;
  [key: string]: unknown;
}

interface GeoJsonFeature {
  type: string;
  properties: GeoJsonFeatureProperties;
  geometry: unknown;
}

interface GeoJsonData {
  type: string;
  features: GeoJsonFeature[];
  [key: string]: unknown;
}

interface AppAction<T = unknown> {
  type: string;
  payload: T;
}

interface UpdatePropertyDetailsTabPayload {
  activeTab: string;
}

interface DownloadSpreadsheetPayload {
  regType: string;
  filterParams: Record<string, unknown>;
}

interface PropertyIndexer {
  indexSingleItem: (p: Property) => void;
  onFinish?: () => SagaIterator;
}

interface PropertyIndexerFactory {
  (): PropertyIndexer;
}

// This is here to help developers with debugging stats changes.
// import { developerTools } from '../debugging';

function* getProperties(): SagaIterator {
  yield getPaginatedData({
    sender: strOracleApiSender,
    path: '/properties',
    getItemsFromResponseData: ({
      propertyList,
    }: {
      propertyList: Property[];
    }) => propertyList,
    serverReturnedPageAction: dataActions.serverReturnedPropertyList,
    *onRetryWithError(): SagaIterator {
      yield put(
        dashboardActions.setLoadingText({
          loadingText: LOADING_SCREEN_REFETCH_TEXT,
        })
      );
    },
  });
}

function* getShapeFile(stats: StatsData): SagaIterator {
  const FIRST_AND_ONLY = 0;
  const shapeJsonUrl = stats.shape;
  try {
    const url: string = yield call(signS3Url, shapeJsonUrl);
    const response: AxiosResponse<GeoJsonData> = yield call(axios.request, {
      url,
    });
    const { data } = response;
    // extract useful data from shapefile
    const { geometry } = data.features[FIRST_AND_ONLY];
    yield put(mapActions.setShapeFile({ shapeFile: geometry }));
  } catch {
    Sentry.captureException(new Error(AlertMsgNoShapeFile));
    toaster.warning(AlertMsgNoShapeFile);
    yield put(mapActions.setShapeFile({ shapeFile: [] }));
  }
}

function* extractHoaZones(zoningFilesData: GeoJsonData[]): SagaIterator {
  // TODO: For dev assuming there will only be one zoning file. Must discuss this with data team
  const [zoningGeoJson] = zoningFilesData;
  const hoaNames = zoningGeoJson?.features.map(
    (feature: GeoJsonFeature) => feature?.properties?.region
  );
  const hoaNamesNoUndefined = compact(hoaNames);
  const stringHoaNames = hoaNamesNoUndefined.map((n: string | number) =>
    n.toString()
  );
  yield put(dataActions.setHoaDataAction({ hoa: stringHoaNames }));
}

function* getZoningFiles(stats: StatsData | null | undefined): SagaIterator {
  if (!stats?.zoningFiles) {
    yield put(mapActions.setZoningFiles({ zoningFiles: null }));
  } else {
    try {
      const zEffects = stats.zoningFiles.map((zoningFileUrl: string) => {
        return call(async () => {
          const signedZoningUrl: string = await signS3Url(zoningFileUrl);
          return axios.request({ url: signedZoningUrl });
        });
      });
      const zResponses: AxiosResponse<GeoJsonData>[] = yield all(zEffects);
      const zoningFilesData = zResponses.map(
        ({ data }: AxiosResponse<GeoJsonData>) => data
      );
      yield call(extractHoaZones, zoningFilesData);
      yield put(mapActions.setZoningFiles({ zoningFiles: zoningFilesData }));
    } catch (err) {
      Sentry.captureException(err);
      console.error('error getting zoning files', err); // eslint-disable-line no-console
    }
  }
}

function* getComplaints(): SagaIterator {
  const strComplaintsAvailable: boolean = yield select(
    // @ts-expect-error Untyped state
    dataSelectors.strComplaintsAvailable
  );
  if (!strComplaintsAvailable) {
    return;
  }

  try {
    yield getPaginatedData({
      path: '/complaints',
      sender: strOracleApiSender,
      getItemsFromResponseData: ({ complaints }: { complaints: Complaint[] }) =>
        complaints,
      serverReturnedPageAction: dataActions.serverReturnedComplaints,
    });
  } catch (err) {
    console.error('error getting complaints', err); // eslint-disable-line no-console
  }
}

async function fetchTranslations(): Promise<AxiosResponse<TranslationsData>> {
  try {
    return await strOracleApiSender.get('/translations', {}, undefined);
  } catch (e) {
    Sentry.captureException(e, { extra: { translationEndpointFailed: true } });
    return { data: {} } as AxiosResponse<TranslationsData>; // Ensure it matches expected structure
  }
}

/**
 * Will fetch stats and user_rights. Stats determines how the application should behave.
 * `user_rights` determines what the user can do.
 */
function* getStatsAndUserRights(): SagaIterator<{
  stats: StatsData;
  userRights: UserRightsData;
}> {
  const statsRequest = strOracleApiSender.get('/stats', {}, undefined);
  const translationsRequest = fetchTranslations();
  const userRightsRequest = strOracleApiSender.get(
    '/user_rights',
    {},
    undefined
  );

  const rentalscapeConfigRequest = ariadneGqlClient.query({
    query: REGION_QUERY,
    variables: { regionId: BaseSender.currentCity },
  });

  // @ts-expect-error Untyped state
  const responses: AxiosResponse<unknown>[] = yield all([
    statsRequest,
    translationsRequest,
    userRightsRequest,
    rentalscapeConfigRequest,
  ]);

  const [stats, translations, userRights, rentalscapeConfigResponseData] =
    responses.map((resp: AxiosResponse<unknown>) => resp.data);

  const rentalscapeConfig = (
    rentalscapeConfigResponseData as {
      region?: { rentalscapeConfig?: RentalscapeConfig };
    }
  )?.region?.rentalscapeConfig;

  // Uncomment the line below and import { developerTools } from '@/debugging'
  // to test changes to stats. The import line itself is in a comment at the top
  // of this file.

  // yield developerTools.mergeLocalStatsSagaInPlace(stats);

  yield put(
    dataActions.serverReturnedRentalscapeConfigData({
      rentalscapeConfig: rentalscapeConfig as RentalscapeConfig,
    })
  );
  yield put(
    userActions.serverReturnedUserRights({
      userRights: userRights as UserRightsData,
    })
  );
  yield put(
    dataActions.serverReturnedTranslationsData({
      translations: translations as TranslationsData,
    })
  );
  yield put(dataActions.serverReturnedStatsData({ stats: stats as StatsData }));

  return {
    stats: stats as StatsData,
    userRights: userRights as UserRightsData,
  };
}

function* indexComplaintsByProperty(): SagaIterator {
  const complaintsData: Complaint[] = yield select(
    dataSelectors.complaintsData
  );

  const complaintsByPropertyAPN = complaintsData.reduce(
    (acc: Record<string, Complaint[]>, complaint: Complaint) => {
      const { apn } = complaint;
      if (!apn) return acc;
      return {
        ...acc,
        [apn]: [...(acc[apn] || []), complaint],
      };
    },
    {}
  );

  yield put(
    dataActions.setComplaintsByProperty({ data: complaintsByPropertyAPN })
  );
}
function indexPropertyViolations(): PropertyIndexer {
  const allViolations = new Set<string>(); // Assuming violations are strings

  function indexSingleItem(p: Property): void {
    // @ts-expect-error Untyped state
    p?.violations?.forEach((violation: string) => allViolations.add(violation));
  }

  function* onFinish(): SagaIterator {
    yield put(
      dataActions.setAllObservedViolationTypes({
        violationTypes: ImmutableSet(allViolations),
      })
    );
  }

  return {
    indexSingleItem,
    onFinish,
  };
}

function indexPropertyPlatforms(): PropertyIndexer {
  const allPlatforms = new Set<string>(); // Assuming platforms are strings

  function indexSingleItem(p: Property): void {
    // @ts-expect-error Untyped state
    p?.onlinePlatforms?.forEach((platform: string) =>
      allPlatforms.add(platform)
    );
  }

  function* onFinish(): SagaIterator {
    yield put(
      dataActions.setAllObservedPlatforms({
        platforms: ImmutableSet(allPlatforms),
      })
    );
  }

  return {
    indexSingleItem,
    onFinish,
  };
}

const PROPERTY_INDEXERS: PropertyIndexerFactory[] = [
  indexPropertyPlatforms,
  indexPropertyViolations,
];

function* runPropertyIndexers(
  indexerFactories: PropertyIndexerFactory[]
): SagaIterator {
  const properties: Property[] = yield select(dataSelectors.propertyList);

  const indexers: PropertyIndexer[] = indexerFactories.map(
    (createIndexer: PropertyIndexerFactory) => createIndexer()
  );

  const indexingFunctions: ((p: Property) => void)[] = indexers.map(
    (indexer: PropertyIndexer) => indexer.indexSingleItem
  );

  const indexFinishCallbacks: (() => SagaIterator)[] = indexers
    .map((indexer: PropertyIndexer) => indexer.onFinish)
    .filter((cb): cb is () => SagaIterator => Boolean(cb)); // Type guard for filter

  properties.forEach((p: Property) =>
    indexingFunctions.forEach((indexSingleItem: (p: Property) => void) =>
      indexSingleItem(p)
    )
  );

  yield all(indexFinishCallbacks.map(call));
}

function* getStatsAndUserRightsWithPlaceFallback(): SagaIterator<
  { stats: StatsData; userRights: UserRightsData } | undefined
> {
  // Allow undefined return on cancel
  try {
    return yield call(getStatsAndUserRights);
  } catch (err) {
    const defaultCityResponse: AxiosResponse<{ place: string }> = yield call(
      [strOracleDefaultApiSender, strOracleDefaultApiSender.getWithToken], // Correct way to call method with context
      STRORACLE_ENDPOINTS.DEFAULT_PLACE
    );
    const defaultPlace = defaultCityResponse?.data?.place;
    const currentPlace = BaseSender.currentCity;

    if (!defaultPlace || defaultPlace === currentPlace) {
      throw err;
    }

    yield put(userActions.setCurrentCityEverywhere({ city: defaultPlace }));
    yield put(push(getPlaceSpecificUrl(defaultPlace, `/${NAV_ITEMS.home}`)));
    yield cancel();

    // This code should be unreachable because the yield to cancel() should never return
    // To satisfy TypeScript about potential return after cancel, though logically unreachable:
    // throw new Error('Failed to cancel saga'); // Or return undefined;
    return undefined; // Should not be reached
  }
}

function* getAllDataSaga(): SagaIterator {
  try {
    yield put(
      dashboardActions.setLoadingText({
        loadingText: LOADING_SCREEN_LOADING_TEXT,
      })
    );
    const result: { stats: StatsData; userRights: UserRightsData } | undefined =
      yield call(getStatsAndUserRightsWithPlaceFallback);
    if (!result) {
      // Saga was cancelled
      return;
    }
    const { stats } = result;

    // Ensure stats is not null/undefined before passing to functions expecting StatsData
    if (stats) {
      yield all([
        call(getProperties),
        call(getComplaints),
        call(getShapeFile, stats),
        call(getZoningFiles, stats),
      ]);
    } else {
      // Handle case where stats might be missing after fallback logic if not cancelled
      // This might involve logging an error or skipping dependent sagas
      console.error(
        'Stats data is missing after fallback, cannot proceed with all data fetching.'
      );
      Sentry.captureMessage(
        'Stats data missing in getAllDataSaga after fallback'
      );
      yield all([
        // Call only non-stats dependent sagas or handle error
        call(getProperties),
        call(getComplaints),
      ]);
    }

    yield all([
      call(indexComplaintsByProperty),
      call(runPropertyIndexers, PROPERTY_INDEXERS),
    ]);
  } catch (err) {
    console.error('Error in getAllDataSaga', err); // eslint-disable-line no-console
    Sentry.captureException(err);
    if (
      err !== ERROR_TYPES.DefaultException &&
      !(err instanceof Error && err.message === 'Failed to cancel saga')
    ) {
      throw ERROR_TYPES.DefaultException;
    } else {
      // If it's the cancellation error or already DefaultException, rethrow or handle appropriately
      throw err;
    }
  }
}

export function* getAllDataWatcher(): SagaIterator {
  yield takeLatest(appActions.GET_ALL_DATA, getAllDataSaga);
}

function* updatePropertyDetailsTabSaga(
  action: AppAction<UpdatePropertyDetailsTabPayload>
): SagaIterator {
  const { activeTab } = action.payload;

  // first update the storage manager so the app can reload and know this current tab
  userSessionMgr.propertyDetailsTab = activeTab;
  // next update the tab in the redux store
  // @ts-expect-error Untyped state
  yield put(dashboardActions.setActivePropertyDetailsTab({ activeTab }));
}

export function* updatePropertyDetailsTabWatcher(): SagaIterator {
  yield takeLatest(
    dashboardActions.updatePropertyDetailsTab.type,
    updatePropertyDetailsTabSaga
  );
}

function* downloadSpreadsheet({
  payload,
}: AppAction<DownloadSpreadsheetPayload>): SagaIterator {
  const { regType, filterParams } = payload;
  const currentCity: string = yield select(userSelectors.currentCity);

  try {
    const result: AxiosResponse<ArrayBuffer> = yield call(
      [strOracleApiSender, strOracleApiSender.get], // Correct way to call method with context
      `/spreadsheet/${regType}`,
      { filterParams },
      'arraybuffer'
    );
    const { data } = result;
    const blob = new Blob([data], {
      type: 'application/vnd.ms-excel',
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${currentCity}-${regType}.xls`;
    link.click();
    link.remove();
  } catch (err) {
    Sentry.captureException(err);
    toaster.error(AlertMsgNoSpreadsheet);
  }
}

export function* downloadSpreasheetWatcher(): SagaIterator {
  // Assuming appActions.DOWNLOAD_SPREADSHEET is an action creator from typesafe-actions or similar
  // If it's just a string constant, appActions.DOWNLOAD_SPREADSHEET would be correct.
  // The error pattern from other watchers suggests `.type` is needed.
  if (typeof appActions.DOWNLOAD_SPREADSHEET === 'string') {
    yield takeLatest(
      appActions.DOWNLOAD_SPREADSHEET as string,
      downloadSpreadsheet
    );
  } else if (
    appActions.DOWNLOAD_SPREADSHEET &&
    // @ts-expect-error Untyped state
    appActions.DOWNLOAD_SPREADSHEET.type
  ) {
    yield takeLatest(
      // @ts-expect-error Untyped state
      appActions.DOWNLOAD_SPREADSHEET.type,
      downloadSpreadsheet
    );
  } else {
    // Fallback or error if the structure is unexpected
    console.error('DOWNLOAD_SPREADSHEET action type cannot be determined');
  }
}
